import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, PlayCircle, Trash2, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Gauge, Zap } from "lucide-react";
import api from "../../lib/api";
import { AdminHeader, Btn, Card, Input, Select } from "../../components/admin/Fields";

/* ------------------------------------------------------------------ */
/* Score gauge                                                         */
/* ------------------------------------------------------------------ */
const ScoreGauge = ({ score = 0, grade = "F" }) => {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const color =
    s >= 90 ? "#0F7B4D" :
    s >= 75 ? "#22346B" :
    s >= 60 ? "#B08A3E" :
    s >= 40 ? "#C97A3E" : "#B24444";
  const circumference = 2 * Math.PI * 52;
  const offset = circumference - (s / 100) * circumference;
  return (
    <div className="flex items-center gap-6" data-testid="seo-score-gauge">
      <svg width="128" height="128" viewBox="0 0 128 128" className="-rotate-90">
        <circle cx="64" cy="64" r="52" stroke="#EFEDE7" strokeWidth="10" fill="none" />
        <circle
          cx="64" cy="64" r="52"
          stroke={color}
          strokeWidth="10"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.7s ease" }}
        />
      </svg>
      <div>
        <div className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium">SEO Skoru</div>
        <div className="font-serif text-5xl text-brand-navy tabular-nums leading-none mt-2">{s}</div>
        <div className="text-sm text-brand-medium mt-2">Not: <span className="font-medium text-brand-navy">{grade}</span></div>
      </div>
    </div>
  );
};

const StatusPill = ({ status }) => {
  if (status === "pass") return <span className="inline-flex items-center gap-1.5 text-[0.66rem] tracking-[0.22em] uppercase text-green-700 bg-green-50 border border-green-200 px-2.5 py-1"><CheckCircle2 size={12} /> Geçti</span>;
  if (status === "warn") return <span className="inline-flex items-center gap-1.5 text-[0.66rem] tracking-[0.22em] uppercase text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1"><AlertTriangle size={12} /> Uyarı</span>;
  return <span className="inline-flex items-center gap-1.5 text-[0.66rem] tracking-[0.22em] uppercase text-red-700 bg-red-50 border border-red-200 px-2.5 py-1"><XCircle size={12} /> Hata</span>;
};

const CATEGORY_LABEL = {
  basic: "Temel Meta",
  content: "İçerik",
  keyword: "Anahtar Kelime",
  media: "Görseller",
  tech: "Teknik",
  social: "Sosyal Medya",
  performance: "Performans",
};

const AdminSeoAnalysisPage = () => {
  const [url, setUrl] = useState("");
  const [focus, setFocus] = useState("");
  const [includePs, setIncludePs] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [pages, setPages] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkPagespeed, setBulkPagespeed] = useState(false);

  const loadPages = async () => {
    try {
      const r = await api.get("/admin/seo/pages");
      setPages(r.data.pages || []);
      if (!url && r.data.pages && r.data.pages.length > 0) {
        setUrl(r.data.pages[0].url);
      }
    } catch (e) {
      // Ignore
    }
  };
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get("/admin/seo/history", { params: { limit: 50 } });
      setHistory(r.data || []);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { loadPages(); loadHistory(); }, []);

  const runAnalyze = async () => {
    if (!url) { toast.error("URL girin"); return; }
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/admin/seo/analyze", {
        url,
        focus_keyword: focus,
        include_pagespeed: includePs,
      });
      setResult(r.data);
      toast.success(`Analiz tamamlandı — Skor ${r.data.score}/100 (${r.data.grade})`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analiz başarısız");
    } finally {
      setRunning(false);
    }
  };

  const openHistory = async (id) => {
    try {
      const r = await api.get(`/admin/seo/history/${id}`);
      setResult(r.data);
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Analiz açılamadı");
    }
  };
  const removeHistory = async (id) => {
    if (!window.confirm("Bu analizi silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/seo/history/${id}`);
    loadHistory();
  };

  const runBulk = async () => {
    if (!window.confirm(`Tüm öncelikli sayfalar analiz edilecek${bulkPagespeed ? " (PageSpeed dahil — birkaç dakika sürebilir)" : ""}. Devam?`)) return;
    setBulkRunning(true);
    setBulkResult(null);
    try {
      const r = await api.post("/admin/seo/analyze-all", { include_pagespeed: bulkPagespeed }, { timeout: 300000 });
      setBulkResult(r.data);
      toast.success(`${r.data.count} sayfa analiz edildi. Ort. skor: ${r.data.average_score}/100`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toplu analiz başarısız");
    } finally { setBulkRunning(false); }
  };

  const groupedChecks = useMemo(() => {
    const g = {};
    (result?.checks || []).forEach((c) => {
      (g[c.category] = g[c.category] || []).push(c);
    });
    return g;
  }, [result]);

  const totals = useMemo(() => {
    const t = { pass: 0, warn: 0, fail: 0 };
    (result?.checks || []).forEach((c) => { t[c.status] = (t[c.status] || 0) + 1; });
    return t;
  }, [result]);

  return (
    <div data-testid="admin-seo-analysis-page">
      <AdminHeader
        eyebrow="Advanced SEO"
        title="SEO Analiz Panosu"
        action={
          <div className="text-xs text-brand-medium max-w-md text-right">
            Yoast/RankMath tarzında; teknik + içerik + anahtar kelime kontrolü, PageSpeed entegrasyonu ve AI önerileri.
          </div>
        }
      />

      {/* Runner */}
      <Card title="Yeni Analiz">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-6">
            <Input label="URL" value={url} onChange={setUrl} placeholder="https://firatconstruction.com/…" testId="seo-url-input" />
            {pages.length > 0 ? (
              <div className="mt-3">
                <Select
                  label="Hızlı Seç"
                  value={url}
                  onChange={setUrl}
                  options={[{ value: "", label: "— Sayfa seç —" }, ...pages.map((p) => ({ value: p.url, label: `${p.path}  ·  ${p.url}` }))]}
                  testId="seo-quick-page"
                />
              </div>
            ) : null}
          </div>
          <div className="md:col-span-4">
            <Input label="Odak Anahtar Kelime" value={focus} onChange={setFocus} placeholder="örn. Arnavutköy arsa yatırımı" testId="seo-focus-input" />
          </div>
          <div className="md:col-span-2 flex items-end gap-2">
            <label className="flex items-center gap-2 text-xs text-brand-medium">
              <input type="checkbox" checked={includePs} onChange={(e) => setIncludePs(e.target.checked)} data-testid="seo-pagespeed-toggle" />
              PageSpeed dahil
            </label>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Btn onClick={runAnalyze} disabled={running} testId="seo-run-btn">
            {running ? <><RefreshCw size={14} className="animate-spin" /> Analiz ediliyor…</> : <><PlayCircle size={14} /> Analizi Başlat</>}
          </Btn>
          <Btn variant="ghost" onClick={loadHistory} testId="seo-refresh-history"><RefreshCw size={14} /> Geçmişi Yenile</Btn>
        </div>
      </Card>

      {/* Bulk analyze */}
      <div className="mt-6">
        <Card title={<span className="inline-flex items-center gap-2"><Zap size={16} className="text-brand-gold" /> Toplu Analiz (Tüm Öncelikli Sayfalar)</span>}>
          <p className="text-sm text-brand-medium mb-4">
            Tek tıkla tüm ana sayfaları ({(pages || []).length} sayfa) analiz eder ve ortalama skor + en iyi/en kötü sayfayı raporlar. PageSpeed açıksa her sayfa için ~20-30 sn ekstra sürer.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-brand-medium">
              <input type="checkbox" checked={bulkPagespeed} onChange={(e) => setBulkPagespeed(e.target.checked)} data-testid="seo-bulk-pagespeed" />
              PageSpeed dahil et (yavaş)
            </label>
            <Btn onClick={runBulk} disabled={bulkRunning} testId="seo-bulk-btn">
              {bulkRunning ? <><RefreshCw size={14} className="animate-spin" /> Toplu analiz çalışıyor…</> : <><Zap size={14} /> Tüm Sayfaları Analiz Et</>}
            </Btn>
          </div>

          {bulkResult ? (
            <div className="mt-6" data-testid="seo-bulk-result">
              {(bulkResult.results || []).some(r => (r.counts?.word_count ?? 0) < 50) ? (
                <div className="mb-6 bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                  <strong>Not:</strong> Bazı sayfalar çok az kelime içeriyor. Bu, React SPA olduğu için sunucudan gelen HTML&apos;de içerik render edilmemesinden kaynaklanır. <strong>Meta/Title/OG etiketleri</strong> yine de doğru analiz ediliyor. Tam içerik analizi için SSR/prerender gerekir.
                </div>
              ) : null}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="border border-black/10 p-4">
                  <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-medium">Ortalama Skor</div>
                  <div className="font-serif text-4xl text-brand-navy mt-1 tabular-nums">{bulkResult.average_score}/100</div>
                  <div className="text-xs text-brand-medium mt-1">{bulkResult.count} sayfa analiz edildi</div>
                </div>
                {bulkResult.best ? (
                  <div className="border border-green-200 bg-green-50 p-4">
                    <div className="text-[0.62rem] tracking-[0.28em] uppercase text-green-700">En İyi Sayfa</div>
                    <div className="font-serif text-xl text-brand-navy mt-1 truncate">{bulkResult.best.path}</div>
                    <div className="text-2xl font-serif mt-1 tabular-nums text-green-700">{bulkResult.best.score}/100 · {bulkResult.best.grade}</div>
                  </div>
                ) : null}
                {bulkResult.worst ? (
                  <div className="border border-red-200 bg-red-50 p-4">
                    <div className="text-[0.62rem] tracking-[0.28em] uppercase text-red-700">İyileştirme Gerekli</div>
                    <div className="font-serif text-xl text-brand-navy mt-1 truncate">{bulkResult.worst.path}</div>
                    <div className="text-2xl font-serif mt-1 tabular-nums text-red-700">{bulkResult.worst.score}/100 · {bulkResult.worst.grade}</div>
                  </div>
                ) : null}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[0.62rem] tracking-[0.24em] uppercase text-brand-medium border-b border-black/10">
                    <tr>
                      <th className="text-left px-3 py-3">Sayfa</th>
                      <th className="text-right px-3 py-3">Skor</th>
                      <th className="text-right px-3 py-3">Not</th>
                      <th className="text-right px-3 py-3">Kelime</th>
                      <th className="text-right px-3 py-3">H1</th>
                      <th className="text-right px-3 py-3">ALT eksik</th>
                      <th className="text-right px-3 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bulkResult.results || []).map((r, i) => (
                      <tr key={i} className="border-b border-black/5 hover:bg-brand-light/60">
                        <td className="px-3 py-3">
                          <div className="text-brand-navy font-medium">{r.path}</div>
                          <div className="text-xs text-brand-medium break-all">{r.url}</div>
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums font-medium" style={{ color: (r.score || 0) >= 75 ? "#0F7B4D" : (r.score || 0) >= 50 ? "#B08A3E" : "#B24444" }}>{r.score}</td>
                        <td className="px-3 py-3 text-right">{r.grade}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{r.counts?.word_count ?? "—"}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{r.counts?.h1_count ?? "—"}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{r.counts?.images_missing_alt ?? "—"}</td>
                        <td className="px-3 py-3 text-right">
                          {r.id ? <button className="text-brand-navy hover:text-brand-gold text-xs tracking-[0.2em] uppercase" onClick={() => openHistory(r.id)} data-testid={`seo-bulk-open-${i}`}>Detay</button> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </Card>
      </div>

      {/* Result */}
      {result ? (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="seo-result">
          <div className="lg:col-span-8 space-y-6">
            <Card>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="min-w-0 flex-1">
                  <div className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium">Analiz Sonucu</div>
                  <div className="font-serif text-lg text-brand-navy mt-2 break-all">{result.url}</div>
                  <div className="text-xs text-brand-medium mt-1">
                    {(result.fetched_at || "").substring(0, 19).replace("T", " ")} · Odak: <strong>{result.focus_keyword || "—"}</strong>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2 text-[0.66rem] tracking-[0.22em] uppercase">
                    <span className="text-green-700 bg-green-50 border border-green-200 px-2 py-1">Geçti · {totals.pass || 0}</span>
                    <span className="text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1">Uyarı · {totals.warn || 0}</span>
                    <span className="text-red-700 bg-red-50 border border-red-200 px-2 py-1">Hata · {totals.fail || 0}</span>
                  </div>
                </div>
                <ScoreGauge score={result.score} grade={result.grade} />
              </div>
            </Card>

            {/* PageSpeed */}
            {result.pagespeed?.available ? (
              <Card title="Google PageSpeed Insights (mobil)">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { k: "performance", label: "Performance" },
                    { k: "seo", label: "SEO" },
                    { k: "accessibility", label: "Erişilebilirlik" },
                    { k: "best_practices", label: "En İyi Uygulama" },
                  ].map((m) => {
                    const v = result.pagespeed[m.k];
                    const color = v == null ? "#8B8D93" : v >= 90 ? "#0F7B4D" : v >= 50 ? "#B08A3E" : "#B24444";
                    return (
                      <div key={m.k} className="border border-black/10 p-4 text-center">
                        <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-medium">{m.label}</div>
                        <div className="font-serif text-3xl mt-2 tabular-nums" style={{ color }}>{v ?? "—"}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6 text-xs">
                  {[
                    { k: "lcp", label: "LCP" },
                    { k: "fcp", label: "FCP" },
                    { k: "cls", label: "CLS" },
                    { k: "tbt", label: "TBT" },
                    { k: "speed_index", label: "Speed" },
                  ].map((m) => (
                    <div key={m.k} className="border border-black/5 bg-brand-light px-3 py-2">
                      <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-medium">{m.label}</div>
                      <div className="mt-1 text-brand-navy">{result.pagespeed[m.k]?.display || "—"}</div>
                    </div>
                  ))}
                </div>
              </Card>
            ) : (
              <Card title="Google PageSpeed Insights">
                <div className="text-sm text-brand-medium flex items-center gap-2">
                  <Gauge size={16} /> {result.pagespeed?.reason || "PageSpeed atlandı."}
                </div>
                <div className="text-xs text-brand-medium mt-2">
                  API anahtarını .env dosyasına <code>GOOGLE_PAGESPEED_API_KEY</code> olarak ekleyin.
                </div>
              </Card>
            )}

            {/* Checks by category */}
            {Object.keys(groupedChecks).map((cat) => (
              <Card key={cat} title={CATEGORY_LABEL[cat] || cat}>
                <div className="divide-y divide-black/5">
                  {groupedChecks[cat].map((c) => (
                    <div key={c.id} className="py-3 flex items-start gap-4">
                      <div className="pt-0.5"><StatusPill status={c.status} /></div>
                      <div className="min-w-0 flex-1">
                        <div className="text-brand-navy font-medium">{c.title}</div>
                        <div className="text-sm text-brand-medium mt-1">{c.message}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}

            {/* Broken links */}
            {(result.counts?.broken_links || []).length > 0 ? (
              <Card title="Kırık Bağlantılar (örnek)">
                <ul className="space-y-2 text-sm">
                  {(result.counts.broken_links || []).map((b, i) => (
                    <li key={i} className="flex items-center justify-between gap-4 border-b border-black/5 pb-2">
                      <span className="text-brand-dark break-all">{b.url}</span>
                      <span className="text-red-600 text-xs tracking-[0.22em] uppercase">{b.status || "ERR"}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}
          </div>

          <div className="lg:col-span-4 space-y-6">
            {/* AI recommendations */}
            <Card title={<span className="inline-flex items-center gap-2"><Sparkles size={16} className="text-brand-gold" /> AI Önerileri</span>}>
              {result.ai_recommendations ? (
                <div className="text-sm text-brand-dark whitespace-pre-line leading-relaxed" data-testid="seo-ai-recs">
                  {result.ai_recommendations}
                </div>
              ) : (
                <div className="text-sm text-brand-medium">AI önerileri kullanılamıyor. EMERGENT_LLM_KEY tanımlı olduğundan emin olun.</div>
              )}
            </Card>

            {/* Snapshot */}
            <Card title="Sayfa Özeti">
              <dl className="text-sm space-y-2">
                <div className="grid grid-cols-3 gap-2"><dt className="text-brand-medium col-span-1">HTTP</dt><dd className="col-span-2">{result.counts?.http_status}</dd></div>
                <div className="grid grid-cols-3 gap-2"><dt className="text-brand-medium col-span-1">Kelime</dt><dd className="col-span-2">{result.counts?.word_count}</dd></div>
                <div className="grid grid-cols-3 gap-2"><dt className="text-brand-medium col-span-1">H1/H2/H3</dt><dd className="col-span-2">{result.counts?.h1_count}/{result.counts?.h2_count}/{result.counts?.h3_count}</dd></div>
                <div className="grid grid-cols-3 gap-2"><dt className="text-brand-medium col-span-1">Görsel</dt><dd className="col-span-2">{result.counts?.image_count} (ALT eksik: {result.counts?.images_missing_alt})</dd></div>
                <div className="grid grid-cols-3 gap-2"><dt className="text-brand-medium col-span-1">İç link</dt><dd className="col-span-2">{result.counts?.internal_link_count}</dd></div>
                <div className="grid grid-cols-3 gap-2"><dt className="text-brand-medium col-span-1">Dış link</dt><dd className="col-span-2">{result.counts?.external_link_count}</dd></div>
                {result.focus_keyword ? (
                  <div className="grid grid-cols-3 gap-2"><dt className="text-brand-medium col-span-1">KW yoğ.</dt><dd className="col-span-2">%{result.counts?.keyword_density ?? 0} ({result.counts?.keyword_occurrences ?? 0} geçiş)</dd></div>
                ) : null}
              </dl>
            </Card>
          </div>
        </div>
      ) : null}

      {/* History */}
      <div className="mt-10">
        <Card title="Analiz Geçmişi">
          {historyLoading ? (
            <div className="text-brand-medium">Yükleniyor…</div>
          ) : history.length === 0 ? (
            <div className="text-brand-medium">Henüz analiz yapılmadı.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[0.62rem] tracking-[0.24em] uppercase text-brand-medium border-b border-black/10">
                  <tr>
                    <th className="text-left px-3 py-3">Tarih</th>
                    <th className="text-left px-3 py-3">URL</th>
                    <th className="text-left px-3 py-3">Odak</th>
                    <th className="text-right px-3 py-3">Skor</th>
                    <th className="text-right px-3 py-3">Not</th>
                    <th className="text-right px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-b border-black/5 hover:bg-brand-light/60">
                      <td className="px-3 py-3 whitespace-nowrap">{(h.created_at || h.fetched_at || "").substring(0, 16).replace("T", " ")}</td>
                      <td className="px-3 py-3 break-all text-brand-navy cursor-pointer" onClick={() => openHistory(h.id)} data-testid={`seo-hist-open-${h.id}`}>{h.url}</td>
                      <td className="px-3 py-3">{h.focus_keyword || "—"}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{h.score}</td>
                      <td className="px-3 py-3 text-right">{h.grade}</td>
                      <td className="px-3 py-3 text-right">
                        <button className="text-red-500 hover:text-red-700" onClick={() => removeHistory(h.id)} data-testid={`seo-hist-del-${h.id}`}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminSeoAnalysisPage;
